import React, { useEffect, useMemo, useState } from "react";
import { Select } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { get } from "../../../utils/apiUtils";
import { useParams } from "react-router-dom";
import GoBackButtonArrow from "../../../assets/img/goBackArrow.png";

const { Option } = Select;

type ViewMode = "main" | "subcodes";
type DocFilterValue = "all" | string;

type DocItem = {
  document_id: string;
  title?: string;
  document_name?: string;
};

type UsageItem = {
  code_id: string;
  name: string;
  description?: string;
  hash_color?: string;
  count_highlights: number;
  percentage_highlights: number;
};

type CloudItem = {
  codeId: string;
  codeName: string;
  weight: number;
};

const randomPos = () => ({
  top: `${Math.floor(Math.random() * 80)}%`,
  left: `${Math.floor(Math.random() * 90)}%`,
});

const buildCloudItems = (items: UsageItem[], topN: number): CloudItem[] => {
  const normalized = Array.isArray(items) ? items : [];
  const sorted = [...normalized].sort(
    (a, b) => (b.count_highlights ?? 0) - (a.count_highlights ?? 0)
  );
  const sliced = sorted.slice(0, topN);

  return sliced.map((x) => ({
    codeId: x.code_id,
    codeName: x.name,
    weight: Number(x.count_highlights ?? 0),
  }));
};

const CodeCloud: React.FC = () => {
  const { t } = useTranslation("docProcessingCodes");
  const { t: tCommon } = useTranslation("common");
  const [topCount, setTopCount] = useState<number>(10);

  // Documents
  const [docsLoading, setDocsLoading] = useState(false);
  const [documents, setDocuments] = useState<DocItem[]>([]);
  const [selectedDocId, setSelectedDocId] = useState<DocFilterValue>("all");

  // View mode + selected main code
  const [viewMode, setViewMode] = useState<ViewMode>("main");
  const [selectedMainCode, setSelectedMainCode] = useState<{
    codeId: string;
    codeName: string;
  } | null>(null);

  // Data
  const [cloudItems, setCloudItems] = useState<CloudItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { id: projectId } = useParams();

  const isAllDocs = selectedDocId === "all";

  const buildDocQuery = () => {
    // document_ids OPTIONAL -> kur all docs, mos e dergo fare
    if (isAllDocs) return "";
    return `?document_ids=${encodeURIComponent(selectedDocId)}`;
  };

  const buildSubcodesQuery = () => {
    // project_id required + document_ids optional
    const base = `?project_id=${encodeURIComponent(String(projectId))}`;
    if (isAllDocs) return base;
    return `${base}&document_ids=${encodeURIComponent(selectedDocId)}`;
  };

  // 1) Fetch documents list
  useEffect(() => {
    if (!projectId) return;

    const fetchDocs = async () => {
      setDocsLoading(true);
      try {
        const res = await get<any>(
          `/documents?project_id=${projectId}&skip=0&limit=1000`
        );
        const items: DocItem[] = res?.data?.items || [];
        setDocuments(items);

        // default gjithmon "All documents"
        setSelectedDocId("all");
      } catch (e) {
        console.error("Failed to fetch documents:", e);
        setDocuments([]);
        setSelectedDocId("all");
      } finally {
        setDocsLoading(false);
      }
    };

    fetchDocs();

    // reset when project changes
    setViewMode("main");
    setSelectedMainCode(null);
    setCloudItems([]);
    setErrorMsg(null);
  }, [projectId]);

  // 2) If document changes -> reset to main view
  useEffect(() => {
    setViewMode("main");
    setSelectedMainCode(null);
  }, [selectedDocId]);

  // 3) Fetch MAIN codes (doc optional)
  useEffect(() => {
    if (!projectId) {
      setCloudItems([]);
      return;
    }
    if (viewMode !== "main") return;

    const fetchMain = async () => {
      setLoading(true);
      setErrorMsg(null);

      try {
        const res = await get<any>(
          `/projects/${projectId}/codes/hierarchical-usage${buildDocQuery()}`
        );

        const items: UsageItem[] = res?.data?.items || [];
        setCloudItems(buildCloudItems(items, topCount));
      } catch (err: any) {
        console.error("Failed to fetch main codes:", err);
        setErrorMsg(err?.message || "Failed to fetch data");
        setCloudItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMain();
  }, [projectId, selectedDocId, topCount, viewMode]);

  // 4) Fetch SUBCODES (project_id required, doc optional)
  useEffect(() => {
    if (!projectId) return;
    if (viewMode !== "subcodes" || !selectedMainCode?.codeId) return;

    const fetchSubcodes = async () => {
      setLoading(true);
      setErrorMsg(null);

      try {
        const res = await get<any>(
          `/codes/${
            selectedMainCode.codeId
          }/subcodes-usage${buildSubcodesQuery()}`
        );

        const items: UsageItem[] = res?.data?.items || [];
        setCloudItems(buildCloudItems(items, topCount));
      } catch (err: any) {
        console.error("Failed to fetch subcodes:", err);
        setErrorMsg(err?.message || "Failed to fetch subcodes");
        setCloudItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSubcodes();
  }, [projectId, selectedDocId, selectedMainCode, topCount, viewMode]);

  const docOptions = useMemo(() => {
    const mapped = documents.map((d) => ({
      value: d.document_id,
      label: d.title || d.document_name || d.document_id,
    }));

    return [{ value: "all", label: tCommon("allDocuments") }, ...mapped];
  }, [documents, tCommon]);

  // Stabilizo pozicionet qe mos me u ndryshu ne cdo render
  const itemsWithStyle = useMemo(() => {
    const list = Array.isArray(cloudItems) ? cloudItems : [];

    return list.map((item, index) => ({
      item,
      rank: index + 1,
      total: list.length,
      pos: randomPos(),
      rot: Math.floor(Math.random() * 20) - 10,
    }));
  }, [cloudItems]);

  const title = useMemo(() => {
    if (viewMode === "subcodes" && selectedMainCode?.codeName) {
      return t("codeCloud.subcodesOf", { name: selectedMainCode.codeName });
    }
    return "";
  }, [viewMode, selectedMainCode, t]);

  const handleWordClick = (codeId: string, codeName: string) => {
    if (viewMode !== "main") return;
    setSelectedMainCode({ codeId, codeName });
    setViewMode("subcodes");
  };

  return (
    <div
      style={{
        background: "#fff",
        padding: 20,
        borderRadius: 10,
        boxShadow: "0 0 5px rgba(0,0,0,0.1)",
        position: "relative",
        height: 420,
        overflow: "hidden",
      }}
    >
      {/* Controls */}
      <div
        style={{
          marginBottom: 20,
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        {/* Top N */}
        <Select
          value={String(topCount)}
          onChange={(value) => setTopCount(Number(value))}
          style={{ width: 180 }}
        >
          <Option value="10">{tCommon("top10Codes")}</Option>
          <Option value="20">{tCommon("top20Codes")}</Option>
        </Select>

        {/* Documents dropdown */}
        <Select
          value={selectedDocId}
          onChange={(val) => setSelectedDocId(val)}
          style={{ width: 260 }}
          placeholder={docsLoading ? tCommon("loadingDocuments") : tCommon("allDocuments")}
          loading={docsLoading}
          showSearch
          optionFilterProp="children"
        >
          {docOptions.map((o) => (
            <Option key={o.value} value={o.value}>
              {o.label}
            </Option>
          ))}
        </Select>

        {loading && <span>{t("codeCloud.loading")}</span>}
        {!loading && errorMsg && (
          <span style={{ color: "#d9363e" }}>{errorMsg}</span>
        )}
        {!loading && !errorMsg && cloudItems.length === 0 && (
          <span style={{ color: "#8c8c8c" }}>{t("codeCloud.noData")}</span>
        )}
      </div>

      {/* Title (vetem ne subcodes) */}
      {viewMode === "subcodes" && (
        <div
          style={{ margin: "4px 0 10px", fontWeight: 600, color: "#722ED1" }}
        >
          {title}
        </div>
      )}

      {/* Cloud */}
      <div
        style={{
          position: "relative",
          width: "100%",
          height: 300,
          padding: "2rem 3rem",
          overflow: "hidden",
          border: "1px solid transparent",
        }}
      >
        {itemsWithStyle.map(({ item, rank, total, pos, rot }, idx) => {
          const fontSize = total > 0 ? 30 - rank * (12 / total) : 20;
          const opacity = total > 0 ? 1 - rank * (0.5 / total) : 1;
          const fontWeight =
            rank === 1 ? "bold" : rank <= total * 0.3 ? 600 : 400;

          const color =
            rank === 1
              ? "#722ED1"
              : rank <= total * 0.3
              ? "#8C6BDB"
              : rank <= total * 0.6
              ? "#A88DCE"
              : "#BFBFBF";

          return (
            <span
              key={`${item.codeId}-${idx}`}
              style={{
                position: "absolute",
                ...pos,
                fontSize: `${fontSize}px`,
                fontWeight,
                color,
                opacity,
                whiteSpace: "nowrap",
                transform: `rotate(${rot}deg)`,
                cursor: viewMode === "main" ? "pointer" : "default",
                userSelect: "none",
              }}
              title={item.codeName}
              onClick={() => handleWordClick(item.codeId, item.codeName)}
            >
              {item.codeName}
            </span>
          );
        })}

        {/* Go back */}
        {viewMode === "subcodes" && (
          <button
            type="button"
            onClick={() => {
              setViewMode("main");
              setSelectedMainCode(null);
            }}
            style={{
              position: "absolute",
              left: 24,
              bottom: 20,
              border: "1px solid #722ED1",
              color: "#722ED1",
              background: "#fff",
              borderRadius: 8,
              padding: "10px 16px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <img
              src={GoBackButtonArrow}
              alt={tCommon("goBack")}
              style={{ width: 16, height: 16 }}
            />
            {tCommon("goBack")}
          </button>
        )}
      </div>

      {/* Download icon */}
      <div
        style={{
          position: "absolute",
          top: 20,
          right: 20,
          backgroundColor: "#722ED1",
          borderRadius: 10,
          padding: "10px 14px",
          cursor: "pointer",
        }}
        title={tCommon("download")}
        aria-label={tCommon("download")}
      >
        <DownloadOutlined style={{ color: "#fff", fontSize: 18 }} />
      </div>
    </div>
  );
};

export default CodeCloud;
